import React, { useEffect, useState } from 'react';
import type { AxiosError } from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import institutionService, { type InstitutionDTO } from '../services/institutionService';
import preProjetoService, {
  type PreProjetoDTO, type PreProjetoPayload, type PreProjetoRetificacaoDTO,
} from '../services/preProjetoService';
import parametroSistemaService from '../services/parametroSistemaService';
import funcaoOrcamentariaService, { type FuncaoOrcamentariaDTO } from '../services/funcaoOrcamentariaService';
import { useAuth } from '../context/AuthContext';
import NovaSolicitacaoButton from '../components/NovaSolicitacaoButton';
import { Permission } from '../types/user.types';
import { UI_THEME } from '../ui/theme';
import { toUpperText } from '../utils/formatters';

const money = (value: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
const currentYear = new Date().getFullYear();
const apiError = (error: unknown, fallback: string) =>
  (error as AxiosError<{ error?: string }>)?.response?.data?.error || fallback;
const formatCurrencyInput = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  return (Number(digits) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};
const parseCurrencyInput = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return digits ? Number(digits) / 100 : 0;
};

const defaultExercicio = currentYear + 1;
const CLASSIFICACAO_LABEL: Record<number, string> = { 1: 'Custeio', 2: 'Investimento' };

const emptyForm: PreProjetoPayload = {
  instituicaoId: '', titulo: '', objeto: '', justificativa: '', areaAtuacao: '', funcaoCodigo: '',
  classificacao: undefined,
  valorSolicitado: 0, exercicio: defaultExercicio,
};

const PreProjetoDetailPage: React.FC = () => {
  const { id } = useParams();
  const isNew = !id || id === 'novo';
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const role = String(user?.role || '').toUpperCase();
  const canManage = hasPermission(Permission.MANAGE_PRE_PROJETOS)
    && ['ADMIN', 'OPERADOR'].includes(role);
  const canSelect = hasPermission(Permission.SELECT_PRE_PROJETO);
  const isOperator = role === 'OPERADOR';
  const isSaas = UI_THEME === 'saas';
  const [project, setProject] = useState<PreProjetoDTO | null>(null);
  const [form, setForm] = useState<PreProjetoPayload>(emptyForm);
  const [valorInput, setValorInput] = useState('');
  const [institutions, setInstitutions] = useState<InstitutionDTO[]>([]);
  const [funcoesOrcamentarias, setFuncoesOrcamentarias] = useState<FuncaoOrcamentariaDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [retificacaoAberta, setRetificacaoAberta] = useState(false);
  const [novoValorInput, setNovoValorInput] = useState('');
  const [novaFuncaoCodigo, setNovaFuncaoCodigo] = useState('');
  const [motivo, setMotivo] = useState('');
  const [retificacoes, setRetificacoes] = useState<PreProjetoRetificacaoDTO[]>([]);

  useEffect(() => {
    const requests: Promise<unknown>[] = [funcaoOrcamentariaService.list().then(setFuncoesOrcamentarias)];
    if (canManage) requests.push(institutionService.listForUser().then((values) => {
      setInstitutions(values);
      if (isNew && isOperator && values.length === 1) {
        const linkedInstitution = values[0];
        setForm((current) => current.instituicaoId
          ? current
          : { ...current, instituicaoId: linkedInstitution.institutionId });
      } else if (isNew && isOperator && values.length === 0) {
        setError('Seu usuário não possui uma instituição ativa vinculada.');
      }
    }));
    if (!isNew && id) requests.push(preProjetoService.get(id).then((value) => {
      setProject(value);
      setForm({
        instituicaoId: value.instituicaoId, titulo: value.titulo, objeto: value.objeto,
        justificativa: value.justificativa || '', areaAtuacao: value.areaAtuacao || '', funcaoCodigo: value.funcaoCodigo || '',
        classificacao: value.classificacao,
        valorSolicitado: value.valorSolicitado, exercicio: value.exercicio,
      });
      setValorInput(value.valorSolicitado ? money(value.valorSolicitado) : '');
      setNovaFuncaoCodigo(value.funcaoCodigo || '');
    }));
    Promise.all(requests).catch((e: unknown) => setError(apiError(e, 'Não foi possível carregar a página.'))).finally(() => setLoading(false));
  }, [id, isNew, canManage, isOperator]);

  const isDraft = isNew || project?.etapa === 'RASCUNHO';
  const setField = <K extends keyof PreProjetoPayload>(key: K, value: PreProjetoPayload[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const selectedInstitution = institutions.find((i) => i.institutionId === form.instituicaoId);
  const institutionAreaCodes = selectedInstitution?.areasAtuacao ?? [];
  // O cadastro institucional já grava o código de 2 dígitos (ver CadastroDadosInstitucionaisPage),
  // mas registros antigos podem ter rótulo livre (ex.: "Saúde") em vez do código ("10"). Resolve
  // cada área da instituição individualmente — por código, depois por rótulo (ignorando acento/caixa).
  // Uma área que não resolve para nenhuma linha do catálogo é descartada em vez de virar uma opção
  // fake: selecioná-la geraria um funcaoCodigo que o backend rejeita ao publicar ("Área de atuação
  // inválida ou inativa"), pois instituicoes_areas_atuacao não tem FK para funcoes_orcamentarias.
  const institutionAreaOptions = institutionAreaCodes
    .map((code) => {
      const byCodigo = funcoesOrcamentarias.find((item) => item.codigo === code);
      if (byCodigo) return byCodigo;
      return funcoesOrcamentarias.find((item) => item.descricao.localeCompare(code, 'pt', { sensitivity: 'base' }) === 0) || null;
    })
    .filter((item): item is FuncaoOrcamentariaDTO => item !== null);
  const hasUnresolvedInstitutionAreas = institutionAreaCodes.length > institutionAreaOptions.length;
  const areaOptions = institutionAreaOptions.length > 0 ? institutionAreaOptions : funcoesOrcamentarias;

  const setInstituicaoId = (instituicaoId: string) =>
    setForm((prev) => ({ ...prev, instituicaoId, funcaoCodigo: '', areaAtuacao: '' }));

  const save = async (event: React.FormEvent) => {
    event.preventDefault(); setSaving(true); setError(''); setSuccess('');
    try {
      isNew ? await preProjetoService.create(form) : await preProjetoService.update(id!, form);
      navigate('/dashboard/pre-projetos', { state: { message: 'Rascunho salvo com sucesso.' } });
    } catch (e: unknown) { setError(apiError(e, 'Não foi possível salvar o pré-projeto.')); setSaving(false); }
  };

  const publish = async () => {
    if (!project) return; setSaving(true); setError('');
    try { const value = await preProjetoService.publish(project.id); setProject(value); setSuccess('Pré-projeto publicado no catálogo.'); }
    catch (e: unknown) { setError(apiError(e, 'Não foi possível publicar.')); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    if (!canManage || isNew) return;
    parametroSistemaService.getParametros()
      .then((p) => setRetificacaoAberta(!!p.preProjetoRetificacaoAberta))
      .catch(() => setRetificacaoAberta(false));
  }, [canManage, isNew]);

  const carregarRetificacoes = (projetoId: string) =>
    preProjetoService.listarRetificacoes(projetoId).then(setRetificacoes).catch(() => setRetificacoes([]));

  useEffect(() => {
    if (!isNew && id) carregarRetificacoes(id);
  }, [id, isNew]);

  const retificar = async () => {
    if (!project) return;
    const novoValor = parseCurrencyInput(novoValorInput);
    if (novoValor <= 0) { setError('Informe o novo valor.'); return; }
    if (!novaFuncaoCodigo) { setError('Selecione a área de atuação.'); return; }
    if (motivo.trim().length < 10) { setError('Informe o motivo da retificação (mínimo de 10 caracteres).'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const retificado = await preProjetoService.retificar(project.id, {
        valorSolicitado: novoValor, funcaoCodigo: novaFuncaoCodigo, motivo: motivo.trim(),
      });
      setProject(retificado);
      setValorInput(money(retificado.valorSolicitado));
      setNovoValorInput('');
      setNovaFuncaoCodigo(retificado.funcaoCodigo || '');
      setMotivo('');
      await carregarRetificacoes(project.id);
      setSuccess('Pré-projeto retificado.');
    } catch (e: unknown) { setError(apiError(e, 'Não foi possível retificar o pré-projeto.')); }
    finally { setSaving(false); }
  };

  const select = async () => {
    if (!project) return; setSaving(true); setError('');
    try {
      const selected = await preProjetoService.select(project.id);
      setProject(selected);
      setSuccess('Pré-projeto convertido em emenda. Ela aparece em Emendas vinculadas, abaixo.');
    } catch (e: unknown) { setError(apiError(e, 'Não foi possível selecionar o pré-projeto.')); }
    finally { setSaving(false); }
  };

  const inputClass = 'mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-white';
  const labelClass = 'text-sm font-medium text-slate-700 dark:text-slate-200';
  const primaryClass = `rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${isSaas ? 'bg-teal-600 hover:bg-teal-700' : 'bg-[#1351B4] hover:bg-[#0B3D8F]'}`;

  if (loading) return <div className="w-full text-sm text-slate-500">Carregando...</div>;

  return (
    <div className="w-full space-y-6">
      <button onClick={() => navigate('/dashboard/pre-projetos')} className="text-sm font-medium text-slate-500 hover:text-slate-800 dark:hover:text-white">← Voltar aos pré-projetos</button>
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isSaas ? 'text-teal-600' : 'text-[#1351B4]'}`}>{isNew ? 'Novo cadastro' : project?.instituicaoNome}</p>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">{isNew ? 'Cadastrar pré-projeto' : project?.titulo}</h1>
          {!isNew && <p className="mt-1 text-sm text-slate-500"><span className="font-mono font-semibold">{project?.codigo}</span> · Etapa: {project?.etapa.replaceAll('_', ' ')}</p>}
        </div>
        <div className="flex gap-3">
          {!isNew && project?.etapa === 'PUBLICADO' && (
            <NovaSolicitacaoButton tituloTela="Detalhes do Pré-projeto" className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800" />
          )}
          {canManage && project?.etapa === 'RASCUNHO' && <button onClick={publish} disabled={saving} className={primaryClass}>Publicar no catálogo</button>}
          {canSelect && project?.etapa === 'PUBLICADO' && <button onClick={select} disabled={saving} className={primaryClass}>{saving ? 'Selecionando...' : 'Selecionar pré-projeto'}</button>}
        </div>
      </header>

      {canManage && !isNew && project?.etapa === 'PUBLICADO' && retificacaoAberta && (
        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-800/50 dark:bg-amber-900/20">
          <h2 className="font-semibold text-slate-900 dark:text-white">Retificação</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            O período de retificação está aberto. Nesta etapa só o valor e a área de atuação podem ser alterados, com motivo.
            Valor atual: <strong>{money(project.valorSolicitado)}</strong> · Área atual: <strong>{project.areaAtuacao || 'sem área definida'}</strong>.
          </p>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            <label className={labelClass}>Novo valor *
              <input inputMode="numeric" placeholder="R$ 0,00" value={novoValorInput}
                onChange={(e) => setNovoValorInput(formatCurrencyInput(e.target.value))} className={inputClass} />
            </label>
            <label className={`${labelClass} md:col-span-2`}>Área de atuação *
              <select value={novaFuncaoCodigo} onChange={(e) => setNovaFuncaoCodigo(e.target.value)} className={inputClass}>
                <option value="">Selecione a área de atuação</option>
                {areaOptions.map((item) => <option key={item.codigo} value={item.codigo}>{item.descricao}</option>)}
                {project.funcaoCodigo && !areaOptions.some((item) => item.codigo === project.funcaoCodigo) && (
                  <option value={project.funcaoCodigo}>{project.areaAtuacao || project.funcaoCodigo}</option>
                )}
              </select>
            </label>
            <label className={`${labelClass} md:col-span-3`}>Motivo da alteração *
              <textarea rows={3} maxLength={500} value={motivo} onChange={(e) => setMotivo(e.target.value)} className={inputClass} />
              <span className="mt-1 block text-xs text-slate-500">Mínimo de 10 caracteres. Fica registrado no histórico do pré-projeto.</span>
            </label>
          </div>
          <div className="mt-3">
            <button type="button" onClick={retificar} disabled={saving || !novoValorInput || !novaFuncaoCodigo || motivo.trim().length < 10} className={primaryClass}>
              {saving ? 'Salvando...' : 'Retificar pré-projeto'}
            </button>
          </div>
        </section>
      )}

      {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
      {success && <p className="rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{success}</p>}

      {canManage && isDraft ? (
        <form onSubmit={save} className="space-y-6">
          <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
            <h2 className="font-semibold text-slate-900 dark:text-white">Identificação da proposta</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-4">
              {isOperator && !(isNew && institutions.length > 1) ? (
                <div className={`${labelClass} md:col-span-2`}>Instituição vinculada<div className={`${inputClass} cursor-not-allowed bg-slate-50 text-slate-600 dark:bg-slate-800`}>{institutions.find((i) => i.institutionId === form.instituicaoId)?.razaoSocial || 'Nenhuma instituição vinculada'}</div></div>
              ) : (
                <label className={`${labelClass} md:col-span-2`}>Instituição *<select required value={form.instituicaoId} onChange={(e) => setInstituicaoId(e.target.value)} className={inputClass}><option value="">Selecione</option>{institutions.map((i) => <option key={i.institutionId} value={i.institutionId}>{i.razaoSocial}</option>)}</select></label>
              )}
              <label className={labelClass}>Exercício *<input required min={currentYear} type="number" value={form.exercicio} onChange={(e) => setField('exercicio', Number(e.target.value))} className={inputClass} /></label>
              <label className={labelClass}>Custeio ou Investimento *<select required value={form.classificacao ?? ''} onChange={(e) => setField('classificacao', e.target.value ? Number(e.target.value) : undefined)} className={inputClass}><option value="">Selecione</option>{Object.entries(CLASSIFICACAO_LABEL).map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></label>
              <label className={`${labelClass} md:col-span-4`}>Título *<input required maxLength={200} value={form.titulo} onChange={(e) => setField('titulo', toUpperText(e.target.value))} className={inputClass} /></label>
              <label className={`${labelClass} md:col-span-4`}>Objeto *<textarea required rows={4} value={form.objeto} onChange={(e) => setField('objeto', toUpperText(e.target.value))} className={inputClass} /></label>
              <label className={`${labelClass} md:col-span-4`}>Justificativa<textarea rows={3} maxLength={250} value={form.justificativa} onChange={(e) => setField('justificativa', toUpperText(e.target.value))} className={inputClass} /><span className="mt-1 block text-xs text-slate-500">Se preenchida, use entre 20 e 250 caracteres.</span></label>
            </div>
          </section>
          <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
            <h2 className="font-semibold text-slate-900 dark:text-white">Valor e área de atuação</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <label className={labelClass}>Valor solicitado *<input required inputMode="numeric" placeholder="R$ 0,00" value={valorInput} onChange={(e) => {
                const formatted = formatCurrencyInput(e.target.value);
                setValorInput(formatted);
                setField('valorSolicitado', parseCurrencyInput(formatted));
              }} className={inputClass} /></label>
              <label className={`${labelClass} md:col-span-2`}>Área de atuação *<select required value={form.funcaoCodigo || ''} onChange={(e) => {
                const selected = areaOptions.find((item) => item.codigo === e.target.value);
                setForm((current) => ({ ...current, funcaoCodigo: selected?.codigo || '', areaAtuacao: selected?.descricao || '' }));
              }} className={inputClass}><option value="">Selecione a área de atuação</option>{areaOptions.map((item) => <option key={item.codigo} value={item.codigo}>{item.descricao}</option>)}</select>
                {form.instituicaoId && institutionAreaCodes.length === 0 && (
                  <span className="mt-1 block text-xs text-amber-600 dark:text-amber-400">
                    Esta instituição ainda não tem área de atuação cadastrada — mostrando o catálogo completo. Atualize o cadastro institucional para restringir a lista.
                  </span>
                )}
                {form.instituicaoId && hasUnresolvedInstitutionAreas && (
                  <span className="mt-1 block text-xs text-amber-600 dark:text-amber-400">
                    {institutionAreaOptions.length === 0
                      ? 'Nenhuma área de atuação cadastrada na instituição corresponde ao catálogo vigente — mostrando o catálogo completo. Atualize o cadastro institucional.'
                      : 'Uma ou mais áreas de atuação cadastradas na instituição não correspondem ao catálogo vigente e foram ocultadas desta lista. Atualize o cadastro institucional se necessário.'}
                  </span>
                )}
              </label>
            </div>
          </section>
          <div className="flex justify-end gap-3"><button type="button" onClick={() => navigate('/dashboard/pre-projetos')} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium">Cancelar</button><button disabled={saving || (isOperator && (institutions.length === 0 || !form.instituicaoId))} className={primaryClass}>{saving ? 'Salvando...' : 'Salvar rascunho'}</button></div>
        </form>
      ) : project && (
        <>
          <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
            <div className="grid gap-5 border-b border-slate-100 pb-5 sm:grid-cols-2 lg:grid-cols-4 dark:border-slate-800">
              <div><p className="text-xs uppercase tracking-wide text-slate-500">Exercício</p><p className="mt-1 text-base font-semibold">{project.exercicio}</p></div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Área de atuação</p>
                <p className="mt-1 flex flex-wrap items-center gap-2 text-base font-semibold">
                  {project.areaAtuacao || 'Sem área definida'}
                  {project.categoriaFuncao === 'SAUDE' && (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                      Conta na reserva de Saúde
                    </span>
                  )}
                  {project.categoriaFuncao === 'EDUCACAO' && (
                    <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-800 dark:bg-sky-950 dark:text-sky-300">
                      Conta na reserva de Educação
                    </span>
                  )}
                </p>
              </div>
              <div><p className="text-xs uppercase tracking-wide text-slate-500">Custeio ou Investimento</p><p className="mt-1 text-base font-semibold">{project.classificacao ? CLASSIFICACAO_LABEL[project.classificacao] ?? '—' : '—'}</p></div>
              <div><p className="text-xs uppercase tracking-wide text-slate-500">Instituição</p><p className="mt-1 text-base font-semibold">{project.instituicaoNome}</p></div>
            </div>
            <div className="mt-5 grid gap-5 sm:grid-cols-3">
              <div><p className="text-xs uppercase tracking-wide text-slate-500">Solicitado</p><p className="mt-1 text-xl font-semibold">{money(project.valorSolicitado)}</p></div>
              <div><p className="text-xs uppercase tracking-wide text-slate-500">Contemplado</p><p className="mt-1 text-xl font-semibold text-emerald-700">{money(project.valorContemplado)}</p></div>
              <div><p className="text-xs uppercase tracking-wide text-slate-500">Restante</p><p className="mt-1 text-xl font-semibold text-amber-700">{money(project.valorRestante)}</p></div>
            </div>
            <div className="mt-6 border-t border-slate-100 pt-5 dark:border-slate-800"><h2 className="font-semibold">Objeto</h2><p className="mt-2 whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-300">{project.objeto}</p></div>
            {project.justificativa && <div className="mt-5"><h2 className="font-semibold">Justificativa</h2><p className="mt-2 whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-300">{project.justificativa}</p></div>}
          </section>

          {project.etapa === 'SELECIONADO' && (
            <section className={`rounded-2xl border p-5 ${isSaas ? 'border-teal-200 bg-teal-50/60 dark:border-teal-900 dark:bg-teal-950/30' : 'border-blue-200 bg-blue-50/60 dark:border-blue-900 dark:bg-blue-950/30'}`}>
              <h2 className="font-semibold text-slate-900 dark:text-white">Pré-projeto selecionado</h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Este pré-projeto já foi convertido em emenda; ela está listada em Emendas vinculadas.</p>
              {project.dataSelecao && <p className="mt-3 text-xs text-slate-500">Selecionado em {new Date(project.dataSelecao).toLocaleString('pt-BR')} por {project.selecionadoPor || 'usuário não identificado'}.</p>}
            </section>
          )}

          {retificacoes.length > 0 && (
            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
              <div className="px-5 py-4"><h2 className="font-semibold">Histórico de retificações</h2></div>
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {retificacoes.map((r) => (
                  <div key={r.id} className="space-y-1 px-5 py-3 text-sm">
                    <p className="text-xs text-slate-500">{new Date(r.dataRetificacao).toLocaleString('pt-BR')} · {r.usuario}</p>
                    {r.valorAnterior !== r.valorNovo && <p>Valor: {money(r.valorAnterior)} → <strong>{money(r.valorNovo)}</strong></p>}
                    {r.funcaoCodigoAnterior !== r.funcaoCodigoNovo && <p>Área de atuação: {r.areaAtuacaoAnterior || '—'} → <strong>{r.areaAtuacaoNova || '—'}</strong></p>}
                    <p className="text-slate-600 dark:text-slate-300">Motivo: {r.motivo}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {project.emendas.length > 0 && <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"><div className="px-5 py-4"><h2 className="font-semibold">Emendas vinculadas</h2></div><div className="divide-y divide-slate-100 dark:divide-slate-800">{project.emendas.map((e) => <button key={e.id} onClick={() => navigate(`/dashboard/emendas/${e.id}`)} className="flex w-full items-center justify-between px-5 py-3 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800"><span>{e.codigoOficial || e.id}</span><span className="font-semibold">{money(e.valor)}</span></button>)}</div></section>}
        </>
      )}
    </div>
  );
};

export default PreProjetoDetailPage;
